package org.example.project.engineletter.layouts

import androidx.compose.runtime.Composable
import org.example.project.enginetabular.currentDateDE
import org.example.project.service.LoginService
import org.example.project.yard.yardPinCode
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun WEProzessdokumentationLayout(
    rootElementId: String? = null,
    auftrag: All,
    vorgangslog: List<All>,
    unternehmen: All,
    logoSrc: String,
    kontrollDefinitionen: List<All> = emptyList()
) {
    SupportLayoutSysCustomDocumentContainer(rootElementId = rootElementId) {
        WEVorgangslogPage {
            WEVorgangslogHeader(unternehmen = unternehmen, logoSrc = logoSrc)
            WEVorgangslogTitleRow(auftrag = auftrag)
            WEVorgangslogPartyCards(auftrag)
            WEVorgangslogDivider()
            WEVorgangslogMeta(auftrag = auftrag)
            WEVorgangslogDivider()
            WEVorgangslogTiles(rows = vorgangslog)
            WEVorgangslogSignature()
            WEVorgangslogFooterDivider()
        }
    }
}

@Composable
internal fun WEVorgangslogPage(content: @Composable () -> Unit) {
    Div({
        attr("data-letter-pdf-page", "true")
        attr("data-letter-page-number", "1")
        style {
            width(794.px)
            height(1123.px)
            backgroundColor(Color.white)
            color(Color("#111111"))
            borderRadius(10.px)
            border(1.px, LineStyle.Solid, Color("#d7dbe3"))
            property("box-shadow", "0 30px 70px rgba(0,0,0,0.22)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            property("box-sizing", "border-box")
            property("padding-left", "18mm")
            property("padding-right", "18mm")
            property("padding-top", "42px")
            property("padding-bottom", "48px")
            property("overflow", "hidden")
        }
    }) {
        content()
    }
}

@Composable
internal fun WEVorgangslogHeader(unternehmen: All, logoSrc: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            textAlign("center")
        }
    }) {
        if (logoSrc.isNotBlank()) {
            Img(src = logoSrc, attrs = {
                style {
                    property("max-width", "360px")
                    property("max-height", "150px")
                    property("object-fit", "contain")
                    marginBottom(10.px)
                }
            })
        }
        Div({ style { fontSize(15.px); lineHeight(20.px); marginBottom(16.px) } }) {
            Text(weVorgangslogCompanyHeaderLine(unternehmen))
        }
        Div({
            style {
                width(100.percent)
                height(1.px)
                property("background", "#111111")
                marginBottom(18.px)
            }
        })
    }
}

@Composable
private fun WEVorgangslogTitleRow(auftrag: All) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
            marginTop(55.px)
            marginBottom(27.px)
            textAlign("center")
            fontSize(16.px)
            lineHeight(20.px)
        }
    }) {
        Div({ style { fontSize(30.px); lineHeight(36.px); property("font-weight", "700") } }) {
            Text("Prozessdokumentation Wareneingang")
        }
        WEProzessHeaderMetaRow(auftrag = auftrag)
    }
}

@Composable
internal fun WEProzessHeaderMetaRow(auftrag: All) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(3, 1fr)")
            gap(18.px)
            width(100.percent)
            fontSize(16.px)
            lineHeight(20.px)
        }
    }) {
        WEProzessMetaItem("Lieferschein", auftrag.lieferscheinnummer.ifBlank { "-" })
        WEProzessMetaItem("Datum", currentDateDE())
        WEProzessMetaItem("Uhrzeit", weVorgangslogCurrentTimeDE())
    }
}

@Composable
private fun WEProzessMetaItem(label: String, value: String) {
    Div({
        style {
            padding(8.px, 0.px)
            textAlign("center")
        }
    }) {
        Text("$label: ")
        Span({ style { property("font-weight", "700") } }) {
            Text(value.ifBlank { "-" })
        }
    }
}

private fun weVorgangslogVisibleTiles(rows: List<All>): List<All> {
    var slotGebuchtSeen = false
    return rows
        .filterNot(::weVorgangslogSkipEvent)
        .sortedBy { it.timestamp.ifBlank { it.id } }
        .filter { row ->
            if (row.typ.trim().uppercase() != "SLOT_GEBUCHT") return@filter true
            if (slotGebuchtSeen) return@filter false
            slotGebuchtSeen = true
            true
        }
}

@Composable
internal fun WEVorgangslogPartyCards(auftrag: All) {
    val items = listOf(
        "Empfänger" to weVorgangslogAddressLines(auftrag.empfaenger),
        "Lieferant" to weVorgangslogAddressLines(auftrag.lieferant),
        "Auftraggeber" to weVorgangslogAddressLines(auftrag.auftraggeber),
        "Unternehmer" to weVorgangslogAddressLines(auftrag.unternehmer)
    )
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "1fr 1fr 1fr 1fr")
            border(1.px, LineStyle.Solid, Color("#111111"))
        }
    }) {
        items.forEachIndexed { index, item ->
            Div({
                style {
                    padding(34.px, 14.px, 24.px, 14.px)
                    property("box-sizing", "border-box")
                    if (index < items.lastIndex) property("border-right", "1px solid #111111")
                    minHeight(142.px)
                }
            }) {
                Div({ style { fontSize(15.px); lineHeight(20.px); marginBottom(16.px); property("font-weight", "700") } }) {
                    Text(item.first)
                }
                item.second.forEachIndexed { lineIndex, line ->
                    Div({
                        style {
                            fontSize(if (lineIndex == 0) 15.px else 13.px)
                            lineHeight(if (lineIndex == 0) 20.px else 18.px)
                            property("word-break", "break-word")
                        }
                    }) { Text(line) }
                }
            }
        }
    }
}

@Composable
internal fun WEVorgangslogMeta(auftrag: All) {
    val meta = listOf(
        Triple("images/user.svg", "Fahrer", auftrag.fahrername.ifBlank { auftrag.name }.ifBlank { "-" }),
        Triple("images/lkw.svg", "LKW", auftrag.lkwkennzeichen.ifBlank { "-" }),
        Triple("images/auflieger-dunkel.svg", "Auflieger", auftrag.aufliegerkennzeichen.ifBlank { "-" }),
        Triple("images/pin.svg", "Pin", yardPinCode(auftrag).ifBlank { "-" }),
        Triple("images/toruebersicht.svg", "Tor", auftrag.tor?.toString().orEmpty().ifBlank { "-" })
    )
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(5, 1fr)")
            gap(8.px)
        }
    }) {
        meta.forEach { (icon, label, value) ->
            WEVorgangslogMetaBox(icon = icon, label = label, value = value)
        }
    }
}

@Composable
private fun WEVorgangslogMetaBox(icon: String, label: String, value: String) {
    Div({
        style {
            border(1.px, LineStyle.Solid, Color("#111111"))
            minHeight(58.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            gap(6.px)
            padding(8.px, 10.px)
            property("box-sizing", "border-box")
            property("word-break", "break-word")
            textAlign("center")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                gap(7.px)
                fontSize(13.px)
                lineHeight(17.px)
                property("font-weight", "700")
            }
        }) {
            Img(src = icon, attrs = {
                style {
                    width(24.px)
                    height(22.px)
                    property("object-fit", "contain")
                    property("filter", "brightness(0)")
                }
            })
            Text(label)
        }
        Div({ style { fontSize(12.px); lineHeight(16.px) } }) {
            Text(value.ifBlank { "-" })
        }
    }
}

@Composable
private fun WEVorgangslogTiles(rows: List<All>) {
    val tiles = weVorgangslogVisibleTiles(rows)
    val bearbeitungszeit = weVorgangslogBearbeitungszeitMinuten(rows)
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(4, 1fr)")
            gap(8.px)
        }
    }) {
        tiles.forEach { row ->
            WEVorgangslogBox(value = weVorgangslogNote(row), label = weVorgangslogTime(row))
        }
        if (bearbeitungszeit.isNotBlank()) {
            WEVorgangslogBox(value = "Bearbeitungszeit", label = bearbeitungszeit)
        }
    }
}

@Composable
private fun WEVorgangslogKontrollTiles(auftrag: All, kontrollDefinitionen: List<All>) {
    val tiles = kontrollDefinitionen
        .filter { it.weVorgangslogKontrollLabel().isNotBlank() }
        .map { definition ->
            definition.weVorgangslogKontrollLabel() to weVorgangslogKontrollValue(auftrag, definition).ifBlank { "-" }
        }
    if (tiles.isEmpty()) return

    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(4, 1fr)")
            gap(8.px)
        }
    }) {
        tiles.forEach { (label, value) ->
            WEVorgangslogBox(value = value, label = label)
        }
    }
}

@Composable
internal fun WEQualitaetsdokumentationBox(label: String, value: String) {
    Div({
        style {
            border(1.px, LineStyle.Solid, Color("#111111"))
            minHeight(58.px)
            height(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            property("box-sizing", "border-box")
            property("word-break", "break-word")
            textAlign("center")
        }
    }) {
        Div({
            style {
                padding(8.px, 10.px, 6.px, 10.px)
                flexGrow(1)
                fontSize(12.px)
                lineHeight(16.px)
                property("font-weight", "700")
                textAlign("center")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                property("box-sizing", "border-box")
            }
        }) { Text(label.ifBlank { "-" }) }
        Div({ style { height(1.px); property("background", "#111111") } })
        Div({
            style {
                minHeight(34.px)
                padding(6.px, 10.px, 8.px, 10.px)
                fontSize(14.px)
                lineHeight(18.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                textAlign("center")
                property("font-weight", "700")
                property("box-sizing", "border-box")
            }
        }) { Text(value.ifBlank { "-" }) }
    }
}

@Composable
internal fun WEVorgangslogDivider() {
    Div({
        style {
            height(1.px)
            property("background", "#111111")
            margin(28.px, 0.px)
        }
    })
}

@Composable
private fun WEVorgangslogBox(value: String, label: String) {
    Div({
        style {
            border(1.px, LineStyle.Solid, Color("#111111"))
            minHeight(58.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            property("box-sizing", "border-box")
            property("word-break", "break-word")
        }
    }) {
        Div({
            style {
                padding(8.px, 10.px, 6.px, 10.px)
                fontSize(14.px)
                lineHeight(18.px)
                property("font-weight", "700")
            }
        }) { Text(value.ifBlank { "-" }) }
        Div({ style { height(1.px); property("background", "#111111") } })
        Div({
            style {
                padding(6.px, 10.px, 8.px, 10.px)
                fontSize(12.px)
                lineHeight(16.px)
            }
        }) { Text(label.ifBlank { "-" }) }
    }
}

@Composable
internal fun WEVorgangslogSignature() {
    val user = LoginService.getUser()
    val signatur = user?.signatur.orEmpty()
    val name = user?.let { weVorgangslogUserName(it) }.orEmpty()
    Div({
        style {
            width(220.px)
            marginTop(34.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(5.px)
            textAlign("center")
        }
    }) {
        Div({
            style {
                height(46.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                property("justify-content", "center")
            }
        }) {
            if (signatur.startsWith("data:image/")) {
                Img(src = signatur, attrs = {
                    style {
                        property("max-width", "160px")
                        property("max-height", "44px")
                        property("object-fit", "contain")
                    }
                })
            }
        }
        Div({ style { property("border-top", "1px solid #111111"); lineHeight(17.px); fontSize(12.px) } }) {
            Div { Text(name.ifBlank { "-" }) }
        }
    }
}

@Composable
private fun WEVorgangslogFooterDivider() {
    Div({
        style {
            property("margin-top", "auto")
            paddingTop(14.px)
            property("border-top", "1px solid #111111")
            minHeight(1.px)
        }
    })
}

private fun weVorgangslogUserName(user: All): String =
    listOf(user.vorname, user.nachname).filter { it.isNotBlank() }.joinToString(" ")
        .ifBlank { user.name.ifBlank { user.key.ifBlank { user.id } } }

private fun weVorgangslogAddressLines(value: String): List<String> =
    value
        .split("\n", "|")
        .map { it.trim() }
        .filter { it.isNotBlank() }
        .ifEmpty { listOf("-") }

private fun weVorgangslogCompanyHeaderLine(unternehmen: All): String =
    listOf(
        unternehmen.firma,
        listOf(unternehmen.strasse, unternehmen.hausnummer).filter { it.isNotBlank() }.joinToString(" "),
        listOf(unternehmen.plz, unternehmen.ort).filter { it.isNotBlank() }.joinToString(" ")
    ).filter { it.isNotBlank() }.joinToString(" | ").ifBlank { "-" }

private fun weVorgangslogDocumentCreator(): String =
    LoginService.getUser()?.let { user ->
        listOf(user.vorname, user.nachname).filter { it.isNotBlank() }.joinToString(" ")
            .ifBlank { user.name.ifBlank { user.key.ifBlank { user.id } } }
    }.orEmpty().ifBlank { "-" }

private fun weVorgangslogCurrentTimeDE(): String {
    val date = kotlin.js.Date()
    return "${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}"
}

private fun weVorgangslogTime(row: All): String {
    val recordedTime = listOfNotBlank(
        row.feedUhrzeit,
        row.uhrzeitAnmeldung,
        row.uhrzeitTorvergabe,
        row.torZugewiesenUhrzeit
    ).firstOrNull().orEmpty()
    if (recordedTime.isNotBlank()) return recordedTime

    return row.timestamp.toDoubleOrNull()?.let { millis ->
        val formatter = js("(function(value) { return new Date(value).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }); })")
        formatter(millis) as String
    }.orEmpty().ifBlank { "-" }
}

private fun weVorgangslogNote(row: All): String {
    val typ = row.typ.trim().uppercase()
    return when (typ) {
        "SLOT_GEBUCHT" -> "Auftrag gebucht"
        "LIEFERUNG_ANGEMELDET" -> "WE Angemeldet"
        "LKW_EINGETROFFEN" -> "LKW Eingetroffen"
        "PIN_ABFRAGE" -> "PIN-Abfrage"
        "TOR_ZUGEWIESEN" -> "Tor Zugewiesen"
        "WE_ABGESCHLOSSEN", "WA_LKW_ABGESCHLOSSEN" -> "Abgeschlossen"
        "WE_TEILABSCHLUSS" -> "Teilentladen"
        "WA_LKW_TEILABSCHLUSS" -> "Teilverladen"
        "TOR_FREI" -> "Tor Frei"
        else -> weVorgangslogCleanNote(row).ifBlank { typ.ifBlank { "-" } }
    }
}

private fun weVorgangslogSkipEvent(row: All): Boolean {
    val typ = row.typ.trim().uppercase()
    val note = weVorgangslogCleanNote(row)
    return typ == "PIN_BESTAETIGT" ||
        typ == "WA_KONTROLLIERT" ||
        note.contains("komplett kontrolliert", ignoreCase = true) ||
        note.contains("Auftrag in Bearbeitung", ignoreCase = true)
}

private fun weVorgangslogBearbeitungszeitMinuten(rows: List<All>): String {
    val relevanteRows = rows
        .filterNot(::weVorgangslogSkipEvent)
        .sortedBy { it.timestamp.ifBlank { it.id } }
    val start = relevanteRows
        .firstNotNullOfOrNull { row ->
            val typ = row.typ.trim().uppercase()
            val note = weVorgangslogCleanNote(row)
            if (typ == "LKW_EINGETROFFEN" || note.contains("angemeldet", ignoreCase = true)) {
                weVorgangslogTimestamp(row)
            } else {
                null
            }
        }
    val ende = relevanteRows
        .asReversed()
        .firstNotNullOfOrNull { row ->
            val typ = row.typ.trim().uppercase()
            val note = weVorgangslogCleanNote(row)
            if (typ == "WE_ABGESCHLOSSEN" || typ == "WA_LKW_ABGESCHLOSSEN" || note.contains("abgeschlossen", ignoreCase = true)) {
                weVorgangslogTimestamp(row)
            } else {
                null
            }
        }
    val minuten = if (start != null && ende != null && ende >= start) ((ende - start) / 60000L).toString() else ""
    return minuten.takeIf { it.isNotBlank() }?.let { "$it min" }.orEmpty()
}

private fun weVorgangslogTimestamp(row: All): Long? =
    row.timestamp.toDoubleOrNull()?.toLong()

private fun weVorgangslogCleanNote(row: All): String {
    val ls = row.feedLsNummer.ifBlank { row.lieferscheinnummer }
    val lkw = row.lkwkennzeichen
    var value = row.notiz
        .ifBlank { row.feedEventLabel }
    if (ls.isNotBlank()) value = value.replace(ls, "")
    if (lkw.isNotBlank()) value = value.replace(lkw, "")
    value = value.replace(Regex("\\s+"), " ").trim(' ', '-', '|')
    return when {
        value.contains("angesetzt", ignoreCase = true) -> "Angesetzt"
        value.contains("angemeldet", ignoreCase = true) -> "WE Angemeldet"
        value.contains("eingetroffen", ignoreCase = true) -> "LKW Eingetroffen"
        value.contains("abgeschlossen", ignoreCase = true) -> "Abgeschlossen"
        value.contains("gebucht", ignoreCase = true) -> "Auftrag gebucht"
        else -> value
    }
}

internal fun All.weVorgangslogKontrollLabel(): String =
    kontrollBezeichnung.ifBlank { bezeichnung.ifBlank { id.ifBlank { "Kontrolle" } } }

internal fun weVorgangslogKontrollValue(entry: All, definition: All): String =
    weVorgangslogNormalizeKontrollAnzeige(
        entry.palettenWerte["mobil.kontrolle.${definition.id.ifBlank { definition.kontrollId }}"].orEmpty()
            .ifBlank { entry.palettenWerte[definition.kontrollBezeichnung].orEmpty() }
            .ifBlank { entry.palettenWerte[definition.bezeichnung].orEmpty() },
        definition
    )

internal fun weQualitaetExtraValue(entry: All, key: String): String =
    entry.palettenWerte[key].orEmpty().ifBlank { "0" }

private fun weVorgangslogNormalizeKontrollAnzeige(value: String, definition: All): String {
    if (!definition.kontrollTyp.equals("checkbox", true)) return value
    return when (value.trim().lowercase()) {
        "\u2705", "ja", "true", "1" -> "\u2705"
        "\u2B1C", "nein", "false", "0" -> "\u2B1C"
        else -> value
    }
}

private fun listOfNotBlank(vararg values: String): List<String> =
    values.map { it.trim() }.filter { it.isNotBlank() }
